// client/src/pages/WagersCreate.jsx
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import WagersIntroSlider from "../components/WagersIntroSlider";
import { safeFetch } from "../lib/safeFetch";
import { apiUrl } from "../lib/apiBase";
import { getAuthToken } from "../lib/auth";

const MEMBER_ROUTE = "/membership";

export default function WagersCreate() {
  const nav = useNavigate();

  const [email, setEmail] = useState("");
  const [tag, setTag] = useState("");
  const [opponent, setOpponent] = useState("");

  const [game, setGame] = useState("");
  const [mode, setMode] = useState("");
  const [platform, setPlatform] = useState("");
  const [amountUsd, setAmountUsd] = useState(5);
  const [rules, setRules] = useState("Best of 1; no glitches; fair play.");

  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(false);

  const devMember = process.env.REACT_APP_DEV_FORCE_MEMBER === "1";
  const effectiveMember = devMember ? true : member;

  useEffect(() => {
    try {
      const saved = localStorage.getItem("member_email");
      if (saved) setEmail(saved);
    } catch {}
  }, []);

  useEffect(() => {
    try {
      if (email) localStorage.setItem("member_email", email);
    } catch {}
  }, [email]);

  useEffect(() => {
    const run = async () => {
      if (!email) {
        setMember(false);
        return;
      }
      try {
        const js = await safeFetch(`/api/subscriptions/me?email=${encodeURIComponent(email)}`);
        setMember(!!js?.member);
      } catch {
        setMember(false);
      }
    };
    run();
  }, [email]);

  const trimmedOpponent = opponent.trim();
  const trimmedGame = game.trim();
  const trimmedMode = mode.trim();
  const trimmedPlatform = platform.trim();

  const canSubmit = useMemo(() => {
    return !!email && !!tag && !!trimmedOpponent && !!trimmedGame && !!trimmedMode && !!trimmedPlatform;
  }, [email, tag, trimmedOpponent, trimmedGame, trimmedMode, trimmedPlatform]);

  const submit = async () => {
    if (!effectiveMember) return;
    if (!canSubmit) return;

    try {
      setLoading(true);
      const body = {
        creatorEmail: email.trim(),
        gamerTag: tag.trim(),
        opponent: trimmedOpponent,
        game: trimmedGame,
        mode: trimmedMode,
        platform: trimmedPlatform,
        amountCents: Math.round(Number(amountUsd || 0) * 100),
        rules: rules.trim(),
      };

      const token = getAuthToken();
      const js = await safeFetch(apiUrl("/wagers"), {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(body),
      });

      if (!js?.ok) throw new Error(js?.message || "create_failed");

      const wagerId = js?.wager?.id || js?.wager?._id;
      alert("Wager invite sent. You can fund your creator deposit inside the room now.");
      nav(wagerId ? `/wagers/${wagerId}` : "/wagers");
    } catch (e) {
      alert(e?.message || "Failed to create wager");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page wagers-create-page">
      <div style={{ marginBottom: 18 }}>
        <WagersIntroSlider />
      </div>

      <section style={styles.shellCard}>
        <div style={styles.headerRow}>
          <div>
            <div style={styles.kicker}>Create a Wager</div>
            <h2 style={styles.title}>Build a private member-vs-member wager room</h2>
            <p style={styles.subtitle}>
              Enter the game, mode, platform, wager amount, and your match rules. Invite one active member and create a private wager room between both accounts.
            </p>
          </div>

          <div style={styles.topActions}>
            <Link style={{ ...styles.btn, ...styles.secondaryBtn }} to="/wagers">
              Back to Wagers
            </Link>
          </div>
        </div>

        <div style={styles.topGrid}>
          <div style={styles.infoCard}>
            <div style={styles.infoLabel}>Membership</div>
            <div style={styles.infoValue}>
              {member === null ? "Checking..." : effectiveMember ? "Verified" : "Required"}
            </div>
            <div style={styles.infoHelp}>
              {member === null ? (
                "Checking your active Get Active Membership."
              ) : effectiveMember ? (
                "Your account is verified and can create member wagers."
              ) : (
                <>
                  Active members only.{" "}
                  <Link to={MEMBER_ROUTE} style={styles.inlineLink}>
                    Become a Member
                  </Link>
                </>
              )}
            </div>
          </div>

          <div style={styles.infoCard}>
            <div style={styles.infoLabel}>Room type</div>
            <div style={styles.infoValue}>Private invite</div>
            <div style={styles.infoHelp}>
              Your opponent must already have an account and active membership before the wager can be created.
            </div>
          </div>

          <div style={styles.infoCard}>
            <div style={styles.infoLabel}>Match timer</div>
            <div style={styles.infoValue}>90 minutes</div>
            <div style={styles.infoHelp}>
              Once accepted, the room has a 90-minute completion window before it is voided.
            </div>
          </div>
        </div>

        <div style={styles.formGrid}>
          <div style={styles.mainCard}>
            <div style={styles.sectionTitle}>Match setup</div>

            <div style={styles.fieldGrid}>
              <div style={styles.field}>
                <label style={styles.label}>Your email</label>
                <input
                  className="input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  style={styles.input}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Your gamer tag / ID</label>
                <input
                  className="input"
                  value={tag}
                  onChange={(e) => setTag(e.target.value)}
                  placeholder="Your gamer tag"
                  style={styles.input}
                />
              </div>

              <div style={styles.fieldFull}>
                <label style={styles.label}>Opponent username or email</label>
                <input
                  className="input"
                  value={opponent}
                  onChange={(e) => setOpponent(e.target.value)}
                  placeholder="Opponent username or email"
                  style={styles.input}
                />
                <div style={styles.helpText}>
                  Active members only. The opponent must already exist on the site.
                </div>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Game</label>
                <input
                  className="input"
                  value={game}
                  onChange={(e) => setGame(e.target.value)}
                  placeholder="NBA 2K26"
                  style={styles.input}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Mode</label>
                <input
                  className="input"
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                  placeholder="MyCOURT 1v1"
                  style={styles.input}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Platform</label>
                <input
                  className="input"
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                  placeholder="PS5"
                  style={styles.input}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Wager amount (USD)</label>
                <input
                  className="input"
                  type="number"
                  min="1"
                  value={amountUsd}
                  onChange={(e) => setAmountUsd(e.target.value)}
                  style={styles.input}
                />
              </div>

              <div style={styles.fieldFull}>
                <label style={styles.label}>Rules</label>
                <textarea
                  className="input"
                  rows={5}
                  value={rules}
                  onChange={(e) => setRules(e.target.value)}
                  style={{ ...styles.input, resize: "vertical", minHeight: 120 }}
                />
              </div>
            </div>
          </div>

          <div style={styles.sideCard}>
            <div style={styles.sectionTitle}>Room preview</div>

            <div style={styles.previewBox}>
              <div style={styles.previewRow}>
                <span style={styles.previewLabel}>Type</span>
                <span style={styles.previewValue}>Private Invite</span>
              </div>
              <div style={styles.previewRow}>
                <span style={styles.previewLabel}>Opponent</span>
                <span style={styles.previewValue}>{trimmedOpponent || "—"}</span>
              </div>
              <div style={styles.previewRow}>
                <span style={styles.previewLabel}>Game</span>
                <span style={styles.previewValue}>{trimmedGame || "—"}</span>
              </div>
              <div style={styles.previewRow}>
                <span style={styles.previewLabel}>Mode</span>
                <span style={styles.previewValue}>{trimmedMode || "—"}</span>
              </div>
              <div style={styles.previewRow}>
                <span style={styles.previewLabel}>Platform</span>
                <span style={styles.previewValue}>{trimmedPlatform || "—"}</span>
              </div>
              <div style={styles.previewRow}>
                <span style={styles.previewLabel}>Amount</span>
                <span style={styles.previewValue}>${Number(amountUsd || 0).toFixed(2)}</span>
              </div>
            </div>

            <div style={styles.noticeBox}>
              This version is <strong>active member vs active member only</strong>. Once the other side accepts, both players coordinate inside the wager room and have <strong>90 minutes</strong> to complete the match.
            </div>

            <div style={styles.actionColumn}>
              <button
                className="btn primary"
                onClick={submit}
                disabled={!effectiveMember || !canSubmit || loading}
                style={{
                  ...styles.btn,
                  ...styles.primaryBtn,
                  ...((!effectiveMember || !canSubmit || loading) ? styles.disabledBtn : null),
                }}
              >
                {loading ? "Submitting..." : "Send Invite / Create Wager"}
              </button>

              <Link style={{ ...styles.btn, ...styles.secondaryBtn }} to="/wagers">
                Back to Wagers
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const styles = {
  shellCard: {
    borderRadius: 22,
    padding: 20,
    background: "rgba(15, 10, 14, 0.78)",
    border: "1px solid rgba(255, 30, 30, 0.18)",
    boxShadow: "0 22px 80px rgba(0,0,0,0.55)",
    backdropFilter: "blur(10px)",
    marginBottom: 18,
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 18,
    flexWrap: "wrap",
  },
  kicker: {
    color: "rgba(255,255,255,0.74)",
    textTransform: "uppercase",
    letterSpacing: 1.4,
    fontSize: 12,
    marginBottom: 10,
  },
  title: {
    margin: 0,
    color: "#fff",
    fontSize: 34,
    lineHeight: 1.05,
    fontFamily: 'Georgia, "Times New Roman", serif',
  },
  subtitle: {
    margin: "10px 0 0",
    color: "rgba(235,240,255,0.82)",
    lineHeight: 1.55,
    maxWidth: 860,
  },
  topActions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  topGrid: {
    marginTop: 18,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
  },
  infoCard: {
    borderRadius: 16,
    padding: 16,
    background: "rgba(0,0,0,0.28)",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  infoLabel: {
    color: "rgba(255,255,255,0.70)",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 0.8,
    marginBottom: 8,
  },
  infoValue: {
    color: "#fff",
    fontSize: 22,
    fontWeight: 800,
    marginBottom: 6,
    fontFamily: 'Georgia, "Times New Roman", serif',
  },
  infoHelp: {
    color: "rgba(235,240,255,0.78)",
    lineHeight: 1.45,
    fontSize: 14,
  },
  inlineLink: {
    color: "#fff",
    textDecoration: "underline",
    fontWeight: 700,
  },
  formGrid: {
    marginTop: 18,
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.45fr) minmax(300px, 0.85fr)",
    gap: 16,
  },
  mainCard: {
    borderRadius: 18,
    padding: 18,
    background: "rgba(0,0,0,0.34)",
    border: "1px solid rgba(255,255,255,0.10)",
  },
  sideCard: {
    borderRadius: 18,
    padding: 18,
    background: "rgba(0,0,0,0.34)",
    border: "1px solid rgba(255,255,255,0.10)",
    alignSelf: "start",
  },
  sectionTitle: {
    color: "#fff",
    fontSize: 24,
    fontWeight: 800,
    fontFamily: 'Georgia, "Times New Roman", serif',
    marginBottom: 14,
  },
  fieldGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 14,
  },
  field: {
    minWidth: 0,
  },
  fieldFull: {
    gridColumn: "1 / -1",
    minWidth: 0,
  },
  label: {
    display: "block",
    marginBottom: 8,
    color: "rgba(255,255,255,0.94)",
    fontWeight: 700,
    fontSize: 13,
    textTransform: "uppercase",
    letterSpacing: 0.45,
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(9,9,12,0.72)",
    color: "#fff",
    padding: "14px 16px",
    outline: "none",
    fontSize: 16,
  },
  helpText: {
    marginTop: 6,
    color: "rgba(235,240,255,0.72)",
    fontSize: 13,
  },
  previewBox: {
    borderRadius: 16,
    padding: 16,
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  previewRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    padding: "10px 0",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
  },
  previewLabel: {
    color: "rgba(255,255,255,0.70)",
    fontSize: 13,
    textTransform: "uppercase",
    letterSpacing: 0.45,
  },
  previewValue: {
    color: "#fff",
    fontWeight: 700,
    textAlign: "right",
  },
  noticeBox: {
    marginTop: 14,
    borderRadius: 14,
    padding: "12px 14px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "rgba(235,240,255,0.94)",
    lineHeight: 1.5,
  },
  actionColumn: {
    marginTop: 16,
    display: "grid",
    gap: 10,
  },
  btn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 52,
    padding: "14px 18px",
    borderRadius: 14,
    textDecoration: "none",
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(0,0,0,0.28)",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
  },
  primaryBtn: {
    background: "linear-gradient(180deg, #ff2020 0%, #e10000 100%)",
    border: "1px solid rgba(255,30,30,0.28)",
    boxShadow: "0 14px 30px rgba(200,0,0,0.22)",
  },
  secondaryBtn: {
    background: "rgba(0,0,0,0.28)",
  },
  disabledBtn: {
    background: "linear-gradient(180deg, rgba(120,120,120,0.65) 0%, rgba(80,80,80,0.8) 100%)",
    boxShadow: "none",
    cursor: "not-allowed",
    opacity: 0.85,
  },
};
